import { GamePlayData, QuestEnum, Quest, LocationEnum, GameSpeedTier, TimelineItemEnum, TimelineItem } from '../types'
import { QUESTS, getAvailableChapters } from '../constants/quests'
import { checkUnlockRules } from '../utils'
import { applyMultipliers } from './multipliers'
import { getStory } from '@/constants/story'
import { getRealDurationMinutes } from '@/utils'

export function completeQuestAsAutomation(
  gameData: GamePlayData,
  questKey: QuestEnum,
  onShowStoryModal?: () => void
): GamePlayData {
  const quest = QUESTS[questKey]
  if (!quest || gameData.completedQuests.includes(questKey)) {
    return gameData
  }

  // Check if we're in the correct location for this quest
  if (quest.location !== gameData.currentLocation) {
    return gameData
  }

  // Check if all unlock requirements are met
  if (!checkUnlockRules(quest.unlockRequirements, gameData)) {
    return gameData
  }

  let newData = { ...gameData }
  const CHAPTERS = getAvailableChapters()
  // Update active main quest if this was a main quest
  if (quest.type === 'main') {
    // Set next quest
    const currentChapter = CHAPTERS[quest.chapterIndex - 1]
    if (!currentChapter) {
      newData.activeMainQuest = undefined
    } else {
      const questIndex = currentChapter.quests.indexOf(quest.key)
      if (questIndex < currentChapter.quests.length - 1) {
        newData.activeMainQuest = currentChapter.quests[questIndex + 1] as QuestEnum
      } else {
        newData.completedChapters.push(currentChapter.key as LocationEnum)
        if (quest.chapterIndex < Object.keys(CHAPTERS).length) {
          const nextChapter = CHAPTERS[quest.chapterIndex]
          if (nextChapter) {
            newData.activeMainQuest = nextChapter.quests[0] as QuestEnum
          } else {
            newData.activeMainQuest = undefined
          }
        } else {
          newData.activeMainQuest = undefined
        }
      }
    }
    if (onShowStoryModal) {
      onShowStoryModal()
    }
  }
  // Apply quest effects
  if (quest.effect) {
    if (quest.effect.multipliers) {
      newData = applyMultipliers(newData, quest.effect.multipliers, quest.name, 'quest')
    }
    if (quest.effect.automationUnlocked) {
      newData.automations[quest.effect.automationUnlocked] = {
        ...newData.automations[quest.effect.automationUnlocked],
        isUnlocked: true
      }
    }
    if (quest.effect.upgradePurchased) {
      newData.upgrades[quest.effect.upgradePurchased].isPurchased = true
    }
    if (quest.effect.skillsUnlocked) {
      quest.effect.skillsUnlocked.forEach((skill) => {
        newData.skills[skill].points = 100
        newData.skills[skill].level = 1
        newData.currencies.skill.amount += 100
      })
    }

    // Handle stokeCapUnlocked
    if (quest.effect.stokeCapUnlocked && quest.effect.stokeCapUnlocked > (newData.currencies.stoke.max || 0)) {
      newData.currencies.stoke = {
        ...newData.currencies.stoke,
        max: quest.effect.stokeCapUnlocked
      }
    }

    // Handle gameSpeedUnlocked
    if (quest.effect.gameSpeedUnlocked) {
      const newSpeed = quest.effect.gameSpeedUnlocked
      const newSpeeds = [...(newData.currencies.stoke.unlockedLevels || [])]

      if (!newSpeeds.includes(newSpeed)) {
        newSpeeds.push(newSpeed)
      }

      newData.currencies.stoke = {
        ...newData.currencies.stoke,
        unlockedLevels: newSpeeds.sort((a, b) => a - b)
      }
    }
  }

  newData.completedQuests = [...newData.completedQuests, questKey]

  return newData
}

/**
 * Adds a quest completion and related story items to the timeline
 * This function should only be used for automation to simulate manual quest completion
 * with story items in the timeline
 */
export function addQuestToTimelineForAutomation(gameData: GamePlayData, questKey: QuestEnum): GamePlayData {
  let newData = { ...gameData }
  const STORY = getStory()

  // Find the story item for this quest
  const storyIndex = STORY.findIndex((storyElement) => storyElement.type === 'quest' && storyElement.value === questKey)

  if (storyIndex !== -1) {
    // If found, add it to the timeline
    const timelineItem: TimelineItem = {
      type: 'quest' as TimelineItemEnum,
      contentRef: storyIndex,
      reachedValue: 0,
      gameMinutes: newData.gameMinutes,
      realMinutes: getRealDurationMinutes(newData.tick)
    }

    newData.timeline = [...newData.timeline, timelineItem]

    // Now check if there are any story items that come after this one
    const nextStoryIndex = storyIndex + 1
    if (nextStoryIndex < STORY.length && STORY[nextStoryIndex].type === 'story') {
      // Add the story item to the timeline as well
      const storyTimelineItem: TimelineItem = {
        type: 'story' as TimelineItemEnum,
        contentRef: nextStoryIndex,
        reachedValue: 0,
        gameMinutes: newData.gameMinutes,
        realMinutes: getRealDurationMinutes(newData.tick)
      }

      newData.timeline = [...newData.timeline, storyTimelineItem]
    }
  }

  return newData
}
